import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from '@edx/frontend-platform/i18n';
import { StatefulButton, Hyperlink } from '@edx/paragon';
import messages from './messages';
import { twoFactorResendRequest } from '../login/data/service';
import './twofactor.scss';

function TwoFactorPage({ email, onValidate, onResend, onWrongNumber, intl }) {
  const length = 6;
  const [values, setValues] = useState(Array(length).fill(''));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef([]);

  const focusInput = (idx) => {
    const el = inputsRef.current[idx];
    if (el) el.focus();
  };

  const handleChange = (e, idx) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
    const next = [...values];
    next[idx] = v;
    setValues(next);
    if (v && idx < length - 1) focusInput(idx + 1);
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !values[idx] && idx > 0) {
      focusInput(idx - 1);
    }
    if (e.key === 'ArrowLeft' && idx > 0) focusInput(idx - 1);
    if (e.key === 'ArrowRight' && idx < length - 1) focusInput(idx + 1);
  };

  const handlePaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
    if (!paste) return;
    const next = Array(length).fill('');
    for (let i = 0; i < length && i < paste.length; i += 1) next[i] = paste[i];
    setValues(next);
  };

  const otp = values.join('');
  const isComplete = otp.length === length && !otp.includes('');

  const handleValidateClick = async () => {
    setError('');
    setLoading(true);
    try {
      await Promise.resolve(onValidate(otp));
      // success will redirect; no-op here
    } catch (e) {
      const msg = (e && (e.message || (e.error || (e.responseData && e.responseData.error)))) || intl.formatMessage(messages['invalid.otp'] || { defaultMessage: 'Invalid code' });
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleResendClick = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setResendMessage('');
    setResendLoading(true);
    setLoading(true);
    try {
      // call passed handler if provided
      if (typeof onResend === 'function') onResend();
      const resp = await twoFactorResendRequest(email);
      const msg = (resp && (resp.message || resp.detail || resp.success && 'A new code was sent')) || 'A new code was sent';
      setResendMessage(msg);
    } catch (err) {
      const msg = (err && (err.error || err.message || (err.response && err.response.error))) || 'Failed to resend code';
      setResendMessage(msg);
    } finally {
      setResendLoading(false);
      setLoading(false);
    }
  };

  return (
    <div className="twofactor-root">
      <div className="twofactor-card mw-md text-center">
        <h2 className="twofactor-heading">{intl.formatMessage(messages.title)}</h2>
        <p className="twofactor-subtext">{intl.formatMessage(messages.sentTo, { email })}</p>

        <div className="otp-inputs" onPaste={handlePaste}>
          {values.map((val, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <input
              key={idx}
              type="text"
              inputMode="numeric"
              className="otp-input"
              maxLength={1}
              value={val}
              ref={(el) => { inputsRef.current[idx] = el; }}
              onChange={(e) => handleChange(e, idx)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
            />
          ))}
        </div>

        <div className="mt-3">
          <StatefulButton
            name="validate-otp"
            id="validate-otp"
            type="button"
            variant="brand"
            state={loading ? 'pending' : (isComplete ? 'default' : 'disabled')}
            labels={{ default: intl.formatMessage(messages.validate), pending: '' }}
            onClick={handleValidateClick}
          />
        </div>

        {error ? <div className="text-danger mt-2" role="alert">{error}</div> : null}

        <div className="mt-3">
          <Hyperlink className="d-block one-time-password-link" destination="#" onClick={handleResendClick}>
            {resendLoading ? intl.formatMessage(messages['resend.pending'] || messages.resend) : intl.formatMessage(messages.resend)}
          </Hyperlink>
          {resendMessage ? <div className="mt-2 small text-muted">{resendMessage}</div> : null}
        </div>
      </div>
    </div>
  );
}

TwoFactorPage.propTypes = {
  email: PropTypes.string,
  onValidate: PropTypes.func,
  onResend: PropTypes.func,
  onWrongNumber: PropTypes.func,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }).isRequired,
};

TwoFactorPage.defaultProps = {
  email: '',
  onValidate: (otp) => { console.log('Validate OTP', otp); },
  onResend: () => { console.log('Resend OTP'); },
  onWrongNumber: () => { console.log('Wrong email'); },
};

export default injectIntl(TwoFactorPage);
